/**
 * HTTP auth service — talks to the gateway `/auth/*` endpoints.
 * Auditing is handled server-side in real mode, so no audit writes here.
 *
 * The gateway login/verify responses carry NO user object (and the profile
 * endpoints are currently broken server-side), so the AuthUser is derived from
 * the JWT claims + the login identifier. See mappers.buildAuthUserFromClaims.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { AuthLoginResult, AuthLogoutParams, AuthService } from './auth.service'
import type { AuthUser } from '@/types/auth.types'
import {
  buildAuthUserFromClaims,
  decodeJwtClaims,
  type JwtClaims,
  type RawLoginResponse,
} from './mappers'

export function createHttpAuthService(): AuthService {
  return {
    async login(email, password, mfaCode, rememberMe): Promise<AuthLoginResult> {
      try {
        // LoginRequest.username accepts a username OR an email.
        const res = await apiClient.post<RawLoginResponse>('/auth/login', {
          username: email,
          password,
          mfa_code: mfaCode || undefined,
          remember_me: rememberMe ?? false,
        })
        const data = res.data
        const token = data.access_token ?? data.token ?? ''
        if (!token) throw new Error('Login response missing access token')

        const claims = decodeJwtClaims(token)
        return {
          user: buildAuthUserFromClaims(claims, email, data),
          token,
          refreshToken: data.refresh_token ?? '',
          sessionId: data.session_id ?? claims?.session_id ?? '',
        }
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async validateToken(token): Promise<AuthUser | null> {
      try {
        const res = await apiClient.post<{ valid?: boolean; claims?: JwtClaims }>('/auth/verify', {
          token,
          destination_service:
            decodeJwtClaims(token)?.scope === 'client_vault' || decodeJwtClaims(token)?.tenant_id
              ? 'client-vault'
              : undefined,
        })
        if (res.data?.valid === false || !res.data?.claims) return null
        // Identity only — the stored AuthUser (from login) is the source of
        // profile fields; this just confirms the token is still valid.
        return buildAuthUserFromClaims(res.data.claims)
      } catch {
        return null
      }
    },

    async logout({ sessionId, allDevices }: AuthLogoutParams): Promise<void> {
      try {
        await apiClient.post('/auth/logout', {
          session_id: sessionId ?? undefined,
          revoke_all_sessions: allDevices ?? false,
        })
      } catch {
        // A failed logout server-side should still clear client state.
      }
    },

    async refresh(refreshToken): Promise<{ token: string; refreshToken: string }> {
      try {
        const res = await apiClient.post<RawLoginResponse>('/auth/refresh', {
          refresh_token: refreshToken,
        })
        return {
          token: res.data.access_token ?? res.data.token ?? '',
          refreshToken: res.data.refresh_token ?? refreshToken,
        }
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async forgotPassword(email): Promise<{ resetToken?: string }> {
      // Enumeration-safe: the gateway/security-service always returns 200 with a
      // generic message; never surface a server error that hints at existence.
      try {
        const res = await apiClient.post<{ reset_token?: string }>('/auth/forgot-password', {
          email,
        })
        return { resetToken: res.data?.reset_token ?? undefined }
      } catch {
        return {}
      }
    },

    async resetPassword(token, newPassword): Promise<void> {
      try {
        await apiClient.post('/auth/reset-password', {
          token,
          new_password: newPassword,
        })
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
